import { defineComponent as _defineComponent } from 'vue'
import { withDefaults, computed, ref, nextTick } from 'vue';
import { SnowplowContext } from '@/helpers/tracking/providers/snowplow-contexts';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';
import { ClipProvider, ObjectType, SraEventAction } from '@/@types/graphql-types';
import { YoutubePlayerOptions } from '@/interfaces/components/youtube-player';
import YoutubePlayer from '@/ui-components/YoutubePlayer.vue';
import { SponsoredAdFragment } from '@/features/sponsored-recommendations/graphql/fragments/SponsoredAd.fragment';
import HiddenHorizontalScrollbar from '@/components/HiddenHorizontalScrollbar.vue';
import { TrackingProviderPropertiesInterface } from '@/helpers/tracking/providers';
import { useBackendTracking } from '@/helpers/composables/useBackendTracking';
import { TitleDetail } from '@/interfaces/title-details-graphql';
import DailyMotionPlayer from '@/ui-components/players/DailyMotionPlayer.vue';
import { VideoPlayersTrackingIds } from '@/constants/video-players-tracking-ids';

type TitleDetailClip = TitleDetail['content']['clips'][0];

export default /*#__PURE__*/_defineComponent({
  __name: 'TitleDetailTitleClipsV1',
  props: {
    clips: null,
    titleObjectType: null,
    titleObjectId: null,
    titleId: null,
    source: { default: '' },
    additionalContexts: { default: () => [] },
    influencedSponsoredAd: { default: undefined },
    activeClipId: { default: null },
    mute: { type: Boolean, default: false },
    autoplay: { type: Boolean, default: false },
    limit: { default: 4 },
    isBlocking: { type: Boolean, default: false },
    seasonNumber: { default: null },
    episodeNumber: { default: null }
  },
  emits: ['setActiveClipId'],
  setup(__props: any, { emit }) {

const props = __props as { clips: TitleDetailClip[], titleObjectType: ObjectType | null, titleObjectId: number | null, titleId: string, source: string, additionalContexts: SnowplowContext[], influencedSponsoredAd: SponsoredAdFragment | undefined, activeClipId: string | null, mute: boolean, autoplay: boolean, limit: number, isBlocking: boolean, seasonNumber: number | null, episodeNumber: number | null };





const trackingMap = {
	buffering: 'youtube_buffering',
	playing: 'youtube_playing',
	paused: 'youtube_paused',
	ended: 'youtube_ended',
};

const hasPlayedClips: Record<string, boolean> = {};

const activeVideo = ref<HTMLDivElement | null>(null);

const visibleClips = computed(() => props.clips.slice(0, props.limit));

const hasAutoplay = computed(() => props.limit === 1 && props.autoplay);

const missingThumbnailIds = ref<Record<string, boolean>>({});

const playerVars = computed<YoutubePlayerOptions>(() => ({
	autoplay: hasAutoplay.value ? 1 : 0,
	mute: props.mute ? 1 : 0,
}));

const isActiveClip = (clip: TitleDetailClip) => {
	const id = props.activeClipId?.split(/:(.*)/s); // to split into only array of two strings.
	if (!id) return false;
	return id[0] === props.source && id[1] == clip.externalId;
};

const onActivate = (clipId: string | null) => {
	const id = clipId ? `${props.source}:${clipId}` : null;
	emit('setActiveClipId', id);
};

const { trackSraEvent } = useBackendTracking();

async function onTrack(eventName: keyof typeof trackingMap, clip: TitleDetailClip) {
	const category = trackingMap[eventName];
	if (category) {
		TrackingHelper.trackEvent(
			category,
			{
				action: props.titleObjectType?.toLowerCase(),
				label: props.source,
				nonInteraction: props.autoplay,
				value: VideoPlayersTrackingIds[clip.provider],
			},
			props.additionalContexts
		);
	}

	// track youtube_started only once if the clip is played at least once
	if (eventName === 'playing') {
		await scrollIntoView(activeVideo.value);

		// track influenced campaign metrics
		if (!!props.influencedSponsoredAd?.bidId) {
			trackSraEvent({
				action: SraEventAction.PlayTrailerClicked,
				sponsoredAd: props.influencedSponsoredAd,
				jwEntityID: props.titleId,
			});
		}

		if (!(clip.externalId in hasPlayedClips)) {
			TrackingHelper.trackEvent(
				'youtube_started',
				{
					action: props.titleObjectType?.toLowerCase(),
					label: props.source,
					value: VideoPlayersTrackingIds[clip.provider],
				},
				props.additionalContexts
			);
		}
		hasPlayedClips[clip.externalId] = true;
	}
	// for TrailerAutoplay module
	if (eventName === 'ended') {
		emit('setActiveClipId', null);
	}
}

const trackScroll = (label: string) => {
	const properties: TrackingProviderPropertiesInterface = {
		action: 'horizontal_scroller_clicked',
		property: 'trailers',
		label,
	};
	TrackingHelper.trackEvent('userinteraction', properties, props.additionalContexts);
};

async function scrollIntoView(element: HTMLElement | null) {
	// used in combination with scroll-snap to ensure the active video is always in fully visible
	if (element) {
		// nextTick Ensures the previous active video has time to pause and resize before scrolling
		await nextTick();
		element.scrollIntoView({
			behavior: 'smooth',
			block: 'nearest',
			inline: 'start',
		});
	}
}

function getVideoElement(event: MouseEvent) {
	activeVideo.value = event.currentTarget as HTMLDivElement;
}

function handleMissingImagePreview(clipId: string) {
	missingThumbnailIds.value = {
		...missingThumbnailIds.value,
		[clipId]: true,
	};
}

return { __sfc: true,props, emit, trackingMap, hasPlayedClips, activeVideo, visibleClips, hasAutoplay, missingThumbnailIds, playerVars, isActiveClip, onActivate, trackSraEvent, onTrack, trackScroll, scrollIntoView, getVideoElement, handleMissingImagePreview, ClipProvider, YoutubePlayer, HiddenHorizontalScrollbar, DailyMotionPlayer }
}

})